import { MaskedImage } from "@evergis/sgis/es/symbols/point/MaskedImage";
import { PointSymbol as SPointSymbol } from "@evergis/sgis/es/symbols/point/Point";
import { SquareSymbol as SSquareSymbol } from "@evergis/sgis/es/symbols/point/Square";
import { StaticImageSymbol as SStaticImageSymbol } from "@evergis/sgis/es/symbols/point/StaticImageSymbol";
import { Color } from "@evergis/color";
import { ImagePointSymbolDc, MaskedImagePointSymbolDc, SvgParameterDc } from "@evergis/api";
import { H3Symbol as SH3Symbol } from "@evergis/sgis/es/symbols/H3Symbol";
import { CombinedPolylineSymbol as SCombinedPolylineSymbol } from "@evergis/sgis/es/symbols/CombinedPolylineSymbol";
import { PolylineSymbol } from "@evergis/sgis/es/symbols/PolylineSymbol";
import { ClientSymbol } from "@evergis/react";

import { SGisPolylineSymbol } from "../SGisPolylineSymbol";
import { SGisPolygonSymbol } from "../SGisPolygonSymbol";
import { getOffsetParameterValue } from "../../../evergis/core/classification/classifyOffset";
import { getParameterValue } from "../../../evergis/core/classification/getFromClassified";
import {
  ClusterSymbol,
  CombinedPolylineSymbol,
  H3Symbol,
  isPolygonHasHatchBrush,
  isPolygonHasPatternBrush,
  MaskedImageSymbol,
  MultipointSymbol,
  Offset,
  ParameterValue,
  PointSymbol,
  PolygonSymbol,
  PolylineMiterSymbol,
  PolylineSymbols,
  SquareSymbol,
  StaticImageSymbol,
  StyleSymbolType,
  SvgPointSymbol,
} from "../../../interfaces/style";
import { SGisSymbol } from "./serializeSymbol";
import { SGisBrushFill } from "../SGisBrushFill";
import { SGisImageFill } from "../SGisImageFill";
import { SGisMiterSymbol } from "../SGisMiterSymbol";

export function deserializeSymbol(baseSymbol: StyleSymbolType): SGisSymbol {
  const symbol = centerAlignSymbol(baseSymbol);

  switch (symbol?.type) {
    case "combinedPolylineSymbol":
      return deserializeCombinedPolylineSymbol(symbol as CombinedPolylineSymbol);
    case "h3grid":
      return deserializeH3Symbol(symbol as H3Symbol);
    case "svgPointSymbol":
      return deserializeSvgPointSymbol(symbol as SvgPointSymbol);
    case "circlePointSymbol":
      return deserializePointSymbol(symbol as PointSymbol);
    case "squarePointSymbol":
      return deserializeSquareSymbol(symbol as SquareSymbol);
    case "maskedImagePointSymbol":
      return deserializeMaskedImageSymbol(symbol as ClientSymbol<"maskedImagePointSymbol", MaskedImagePointSymbolDc>);
    case "imagePointSymbol":
      return deserializeImagePointSymbol(symbol as ClientSymbol<"imagePointSymbol", ImagePointSymbolDc>);
    case "polygonSymbol":
      return deserializePolygonSymbol(symbol as PolygonSymbol);
    case "polylineMiterSymbol":
      return deserializeMiterSymbol(symbol as unknown);
    case "simplePolylineSymbol":
    case "polylineSymbol":
      return deserializePolylineSymbol(symbol as PolylineSymbols);
    case "multipointSymbol":
      return deserializePointSymbol((symbol as MultipointSymbol).pointSymbol);
    case "cluster":
      return deserializeSymbol((symbol as ClusterSymbol).singleObjectSymbol as StyleSymbolType);
    default:
      console.warn("Unexpected symbol type", baseSymbol);
  }
}

function deserializePointSymbol(symbol: PointSymbol) {
  return new SPointSymbol(deserializePointSymbolParams(symbol));
}

function deserializeSvgPointSymbol(symbol: SvgPointSymbol) {
  return new SPointSymbol(deserializeSvgPointSymbolParams(symbol));
}

function deserializeSquareSymbol(symbol: SquareSymbol) {
  return new SSquareSymbol({
    ...deserializePointSymbolParams(symbol),
    angle: getParameterValue(symbol.angle),
  });
}

function deserializeMaskedImageSymbol(symbol: MaskedImageSymbol) {
  return new MaskedImage({
    ...symbol,
    angle: getParameterValue(symbol.angle),
    width: getParameterValue(symbol.width),
    height: getParameterValue(symbol.height),
    anchorPoint: deserializeAnchor(symbol.offset),
    imageSource: deserializeBase64(symbol.image),
    maskSource: deserializeBase64(symbol.imageMask),
    maskColor: deserializeColor(getParameterValue(symbol.maskedColor)),
  });
}

function deserializeImagePointSymbol(symbol: StaticImageSymbol) {
  return new SStaticImageSymbol({
    ...symbol,
    angle: getParameterValue(symbol.angle),
    width: getParameterValue(symbol.width),
    height: getParameterValue(symbol.height),
    anchorPoint: deserializeAnchor(symbol.offset),
    source: deserializeBase64(symbol.image),
  });
}

function deserializePolygonSymbol(symbol: PolygonSymbol) {
  if (isPolygonHasPatternBrush(symbol)) {
    return new SGisImageFill(symbol);
  }

  if (isPolygonHasHatchBrush(symbol)) {
    return new SGisBrushFill(symbol);
  }

  return new SGisPolygonSymbol(symbol);
}

function deserializePolylineSymbol(symbol: PolylineSymbols) {
  return new SGisPolylineSymbol(symbol);
}

function deserializeMiterSymbol(symbol: PolylineMiterSymbol) {
  return new SGisMiterSymbol(symbol);
}

function deserializePointSymbolParams({
  size,
  offset,
  strokeColor,
  strokeWidth,
  fillColor,
}: PointSymbol | SquareSymbol) {
  return {
    size: getParameterValue(size),
    offset: getOffsetParameterValue(offset),
    strokeWidth: getParameterValue(strokeWidth),
    strokeColor: deserializeColor(getParameterValue(strokeColor)),
    fillColor: deserializeColor(getParameterValue(fillColor)),
  };
}

function deserializeSvgPointSymbolParams({ type, data, size, offset, angle, background, figure }: SvgPointSymbol) {
  return {
    type: getParameterValue(type),
    data: getParameterValue(data),
    size: getParameterValue(size),
    offset: getOffsetParameterValue(offset),
    angle: getParameterValue(angle),
    ...(background
      ? {
          background: {
            ...(background as SvgParameterDc),
            fillColor: deserializeColor(getParameterValue((background as SvgParameterDc)?.fillColor as string)),
            strokeColor: deserializeColor(getParameterValue((background as SvgParameterDc)?.strokeColor as string)),
            strokeWidth: getParameterValue((background as SvgParameterDc)?.strokeWidth as ParameterValue),
          },
        }
      : {}),
    ...(figure
      ? {
          figure: {
            ...(figure as SvgParameterDc),
            strokeColor: deserializeColor(getParameterValue((figure as SvgParameterDc)?.strokeColor as string)),
            strokeWidth: getParameterValue((figure as SvgParameterDc)?.strokeWidth as ParameterValue),
            fillColor: deserializeColor(
              getParameterValue((figure as SvgParameterDc)?.fillColor as ParameterValue) as string,
            ),
          },
        }
      : {}),
  };
}

function deserializeCombinedPolylineSymbol({ values }: CombinedPolylineSymbol) {
  return new SCombinedPolylineSymbol({
    values: values as unknown as PolylineSymbol[],
  });
}

function deserializeH3Symbol(symbol: H3Symbol) {
  return new SH3Symbol({
    ...symbol,
    cellSize: getParameterValue(symbol.cellSize),
    strokeWidth: getParameterValue(symbol.stroke.width),
    strokeColor: getParameterValue(symbol.stroke.color),
    fillColor: getParameterValue(symbol.fill.color),
  });
}

function deserializeBase64(base: string) {
  return `data:image/png;base64,${base}`;
}

export function deserializeAnchor(offset: Offset) {
  return getOffsetParameterValue(offset);
}

function deserializeColor(color: string): string | null {
  return color ? new Color(color).toString() : null;
}

export function centerAlignSymbol(symbol: StyleSymbolType): StyleSymbolType {
  if (symbol?.type === "circlePointSymbol" || symbol?.type === "squarePointSymbol") {
    // TODO
    // eslint-disable-next-line @typescript-eslint/ban-ts-comment
    // @ts-ignore
    return { ...symbol, offset: [0, 0] };
  }

  if (symbol?.type === "imagePointSymbol" || symbol?.type === "maskedImagePointSymbol") {
    // TODO
    // eslint-disable-next-line @typescript-eslint/ban-ts-comment
    // @ts-ignore
    return { ...symbol, offset: [getParameterValue(symbol.width) / 2, getParameterValue(symbol.height) / 2] };
  }

  return symbol;
}
